import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Cell, ReferenceLine, ScatterChart, Scatter, ZAxis, AreaChart, Area, ReferenceArea, Customized, ComposedChart } from 'recharts';
import { NavBar } from '../components/NavBar';
import { StatCard } from '../components/StatCard';
import { SearchBar } from '../components/SearchBar';
import { ExportButton } from '../components/ExportButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { TableFilter } from '../components/TableFilter';
import { DateRangePicker } from '../components/DateRangePicker';
import { Package, Plus, ArrowUp, ArrowDown, Minus, ChevronDown, Download, Info, Lightbulb, TrendingUp, ArrowUpRight, ArrowRight } from 'lucide-react';
import { tabs, Tab, timeHorizons, TimeHorizon, baseProducts, SalesMode, perfProducts, allSalesOnlyProducts, subscriptionCategories, allSalesCategories, revenueHorizons, RevenueHorizon, revenueProducts, retentionScoreData, tenureDistributionData, waterfallChartData, retentionCurveData, cancellationsByCycle, cancellationsByMonth, cancellationTimingSummary, productLifecycleData, swapDropData, varietalDiversityData, SalesMetric, dailySales, allDailySales, salesRangeDays, formatSalesMoney, formatSalesDay } from '../data/analyticsMockData';
export function AnalyticsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('Sales');
  // Sales State
  const [salesTabMode, setSalesTabMode] = useState<SalesMode>('subscription');
  const [salesDateRange, setSalesDateRange] = useState('Last 30 Days');
  const [salesCustomStartDate, setSalesCustomStartDate] = useState('');
  const [salesCustomEndDate, setSalesCustomEndDate] = useState('');
  const [salesMetric, setSalesMetric] = useState<SalesMetric>('Net');
  const [cancellationBasis, setCancellationBasis] = useState<'Cycles' | 'Months'>('Cycles');
  const [timeHorizon, setTimeHorizon] = useState<TimeHorizon>('Next 30 Days');
  const [searchQuery, setSearchQuery] = useState('');
  // Product Performance State
  const [salesMode, setSalesMode] = useState<SalesMode>('subscription');
  const [dateRange, setDateRange] = useState('Last 30 Days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Products');
  const [perfSearchQuery, setPerfSearchQuery] = useState('');
  // Recurring Revenue State
  const [revenueHorizon, setRevenueHorizon] = useState<RevenueHorizon>('Next 30 Days');
  // Daily sales rows for the selected range, oldest first
  const salesRows = useMemo(() => {
    const source = salesTabMode === 'all' ? allDailySales : dailySales;
    const days = salesRangeDays[salesDateRange] ?? source.length;
    return source.slice(-days).map(row => ({
      ...row,
      net: row.gross - row.refunds
    }));
  }, [salesDateRange, salesTabMode]);
  const salesTotals = useMemo(() => {
    const gross = salesRows.reduce((sum, row) => sum + row.gross, 0);
    const refunds = salesRows.reduce((sum, row) => sum + row.refunds, 0);
    return {
      gross,
      refunds,
      net: gross - refunds,
      refundRate: gross === 0 ? 0 : refunds / gross * 100
    };
  }, [salesRows]);
  const salesChartData = useMemo(() => salesRows.map(row => ({
    date: formatSalesDay(row.date),
    value: salesMetric === 'Gross' ? row.gross : salesMetric === 'Refunds' ? row.refunds : row.net
  })), [salesRows, salesMetric]);
  // Calculate factor based on time horizon
  const factor = useMemo(() => {
    switch (timeHorizon) {
      case 'Next 7 Days':
        return 0.4;
      case 'Next 30 Days':
        return 1.0;
      case 'Next 60 Days':
        return 1.8;
      case 'Next 90 Days':
        return 2.5;
      default:
        return 1.0;
    }
  }, [timeHorizon]);
  // Process data for Stock Forecast
  const processedData = useMemo(() => {
    return baseProducts.map(product => {
      const required = Math.round(product.baseRequired * factor);
      const delta = product.stock - required;
      let status: 'In Stock' | 'Low Stock' | 'Out of Stock' = 'In Stock';
      if (delta < 0) {
        status = 'Out of Stock';
      } else if (delta < required * 0.2) {
        status = 'Low Stock';
      }
      return {
        ...product,
        required,
        delta,
        status
      };
    }).filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase()) || product.sku.toLowerCase().includes(searchQuery.toLowerCase())).sort((a, b) => {
      const statusOrder = {
        'Out of Stock': 0,
        'Low Stock': 1,
        'In Stock': 2
      };
      if (statusOrder[a.status] !== statusOrder[b.status]) {
        return statusOrder[a.status] - statusOrder[b.status];
      }
      return a.delta - b.delta;
    });
  }, [factor, searchQuery]);
  // Calculate stats for Stock Forecast
  const stats = useMemo(() => {
    const totalUnits = Math.round(847 * factor);
    const estimatedRevenue = Math.round(18420 * factor);
    const subscriptionsDue = Math.round(156 * factor);
    return {
      totalUnits,
      productsNeeded: 24,
      estimatedRevenue,
      subscriptionsDue
    };
  }, [factor]);
  // Process data for Product Performance
  const perfData = useMemo(() => {
    let products = [...perfProducts];
    if (salesMode === 'all') {
      products = [...products, ...allSalesOnlyProducts];
    }
    return products.filter(product => {
      const matchesCategory = selectedCategory === 'All Products' || product.category === selectedCategory;
      const matchesSearch = product.name.toLowerCase().includes(perfSearchQuery.toLowerCase()) || product.sku.toLowerCase().includes(perfSearchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    }).sort((a, b) => {
      const revA = salesMode === 'all' ? a.allRevenue : a.revenue;
      const revB = salesMode === 'all' ? b.allRevenue : b.revenue;
      return revB - revA;
    });
  }, [salesMode, selectedCategory, perfSearchQuery]);
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'In Stock':
        return 'success';
      case 'Low Stock':
        return 'warning';
      case 'Out of Stock':
        return 'error';
      default:
        return 'default';
    }
  };
  // Generate Chart Data for Recurring Revenue
  const chartData = useMemo(() => {
    const data = [];
    const today = new Date();
    if (revenueHorizon === 'Next 7 Days' || revenueHorizon === 'Next 30 Days') {
      const days = revenueHorizon === 'Next 7 Days' ? 7 : 30;
      for (let i = 0; i < days; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        const dayOfMonth = date.getDate();
        const isSpikeDay = dayOfMonth === 1 || dayOfMonth === 15;
        // Base values
        let orders = Math.floor(Math.random() * 6) + 3; // 3-8
        let revenue = Math.floor(Math.random() * 1200) + 600; // 600-1800
        // Spike values
        if (isSpikeDay) {
          orders = Math.floor(Math.random() * 7) + 12; // 12-18
          revenue = Math.floor(Math.random() * 1500) + 2500; // 2500-4000
        }
        data.push({
          date: date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          }),
          orders,
          revenue
        });
      }
    } else if (revenueHorizon === 'Next 3 Months') {
      for (let i = 0; i < 12; i++) {
        const orders = Math.floor(Math.random() * 20) + 40; // 40-60 per week
        const revenue = Math.floor(Math.random() * 4000) + 8000; // 8000-12000
        data.push({
          date: `Week ${i + 1}`,
          orders,
          revenue
        });
      }
    } else {
      // Next 12 Months
      for (let i = 0; i < 12; i++) {
        const date = new Date(today);
        date.setMonth(today.getMonth() + i);
        const orders = Math.floor(Math.random() * 50) + 150; // 150-200 per month
        const revenue = Math.floor(Math.random() * 10000) + 35000; // 35000-45000
        data.push({
          date: date.toLocaleDateString('en-US', {
            month: 'short',
            year: '2-digit'
          }),
          orders,
          revenue
        });
      }
    }
    return data;
  }, [revenueHorizon]);
  // Calculate Stats for Recurring Revenue
  const revenueStats = useMemo(() => {
    let forecastRevenue = 38330;
    let renewalOrders = 172;
    let avgRenewalAmount = 222.85;
    if (revenueHorizon === 'Next 7 Days') {
      forecastRevenue = 8950;
      renewalOrders = 42;
      avgRenewalAmount = 213.1;
    } else if (revenueHorizon === 'Next 3 Months') {
      forecastRevenue = 118500;
      renewalOrders = 540;
      avgRenewalAmount = 219.44;
    } else if (revenueHorizon === 'Next 12 Months') {
      forecastRevenue = 485000;
      renewalOrders = 2150;
      avgRenewalAmount = 225.58;
    }
    return {
      forecastRevenue,
      renewalOrders,
      avgRenewalAmount
    };
  }, [revenueHorizon]);
  // Calculate Table Data for Recurring Revenue
  const revenueTableData = useMemo(() => {
    let multiplier = 1;
    if (revenueHorizon === 'Next 7 Days') multiplier = 0.23;
    if (revenueHorizon === 'Next 3 Months') multiplier = 3;
    if (revenueHorizon === 'Next 12 Months') multiplier = 12;
    return revenueProducts.map(product => ({
      ...product,
      forecastUnits: Math.round(product.baseForecastUnits * multiplier),
      forecastRevenue: Math.round(product.baseForecastRevenue * multiplier)
    }));
  }, [revenueHorizon]);
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-6789" data-mp="26nn97e79ifb7">
      <div className="mb-6" data-id="element-6790" data-mp="26mucl620p66s">
        <NavBar tabs={[]} title="Analytics" subtitle="Insights across stock, products, revenue, retention, and wine journey" data-id="element-6791" data-mp="26lj5oxd9zibg" />
      </div>

      {/* Tab Bar */}
      <div className="border-b border-[#E2E8F0] mb-6" data-id="element-6792" data-mp="2zbj4ceh6nz5v">
        <div className="flex gap-6" data-id="element-6793" data-mp="a8r1t4n2y28a">
          {tabs.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-sm font-medium relative transition-colors ${activeTab === tab ? 'text-[#0EA5E9]' : 'text-[#475569] hover:text-[#0F172A]'}`} data-id="element-6794" data-mp="a9ahsft66gjo">
              {tab}
              {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0EA5E9] rounded-t-full" data-id="element-6795" data-mp="f83onpdyb9av" />}
            </button>)}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'Sales' ? <div className="space-y-6" data-id="element-6796" data-mp="o8tbw4p5brcz">
          {/* Controls Row */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-6797" data-mp="o6fjy4priv9n">
            <div className="bg-[#E2E8F0] p-1 rounded-lg inline-flex" data-id="element-6798" data-mp="12wmrhpe8etvk">
              {['Last 7 Days', 'Last 30 Days', 'Last 90 Days', 'This Year', 'Custom'].map(range => <button key={range} onClick={() => setSalesDateRange(range)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${salesDateRange === range ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#475569]'}`} data-id="element-6799" data-mp="1bxcnou7emm9t">
                  {range}
                </button>)}
            </div>

            <div className="flex items-center gap-3" data-id="element-6800" data-mp="1vj4pw9u5ytl0">
              <div className="relative" data-id="element-6801" data-mp="1hkobrxtz20su">
                <select value={salesTabMode} onChange={e => setSalesTabMode(e.target.value as SalesMode)} className="appearance-none bg-white border border-[#CBD5E1] rounded-md pl-4 pr-10 py-1.5 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] cursor-pointer" data-id="element-6802" data-mp="1hjva3b9ve1hk">
                  <option value="subscription" data-id="element-6803" data-mp="1cmmpz555vndo">Subscription Sales</option>
                  <option value="all" data-id="element-6804" data-mp="xwxcqbjsnsx1">All Sales</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-6805" data-mp="12uz37hjjuuij" />
              </div>
              <ExportButton onExport={async type => console.log(`Exporting ${type}`)} data-id="element-6806" data-mp="ovzfcc3v2jnf" />
            </div>
          </div>

          {/* Custom Date Range Picker - only when Custom is selected */}
          {salesDateRange === 'Custom' && <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 shadow-sm" data-id="element-6807" data-mp="2vlkonwee8x6p">
              <div className="max-w-sm" data-id="element-6808" data-mp="2qqwvvlccxgmb">
                <DateRangePicker label="Custom date range" startDate={salesCustomStartDate} endDate={salesCustomEndDate} onChange={(start, end) => {
            setSalesCustomStartDate(start);
            setSalesCustomEndDate(end);
          }} data-id="element-6809" data-mp="2ltexupzvolt3" />
              </div>
            </div>}

          {/* Stat Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-6810" data-mp="39kkc50q6761a">
            <StatCard label="Gross Sales" value={formatSalesMoney(salesTotals.gross)} data-id="element-6811" data-mp="35he7tymmboxg" />
            <StatCard label="Refunds" value={formatSalesMoney(salesTotals.refunds)} data-id="element-6812" data-mp="30j2ydp08jgfr" />
            <StatCard label="Net Sales" value={formatSalesMoney(salesTotals.net)} data-id="element-6813" data-mp="30l5pkcd4jfm2" />
            <StatCard label="Refund Rate" value={`${salesTotals.refundRate.toFixed(1)}%`} data-id="element-6814" data-mp="1u0nyw7qt8j05" />
          </div>

          {/* Sales over time */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm min-h-[400px]" data-id="element-6815" data-mp="4m53b9ii5duz">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6" data-id="element-6816" data-mp="9k6k1nsgh13a">
              <h3 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-6817" data-mp="dmjrqhqrj92p">
                Sales over time
              </h3>
              <div className="inline-flex rounded-lg border border-[#E2E8F0] bg-white p-0.5" role="group" aria-label="Sales metric" data-id="element-6818" data-mp="ie1tv94ivfp9">
                {(['Net', 'Gross', 'Refunds'] as const).map(option => <button key={option} type="button" onClick={() => setSalesMetric(option)} aria-pressed={salesMetric === option} className={`px-3 h-8 rounded-md text-[13px] font-medium transition-colors ${salesMetric === option ? 'bg-[#F0F9FF] text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-6819" data-mp="saoa1klwliky">
                    {option}
                  </button>)}
              </div>
            </div>
            <div className="h-[320px] w-full" data-id="element-6820" data-mp="332pjmdrdkeh8">
              <ResponsiveContainer width="100%" height="100%" data-id="element-6821" data-mp="333it3mhpxi32">
                <LineChart data={salesChartData} margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-6822" data-mp="27s159c2zu46w">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-6823" data-mp="2hr05q7dlutit" />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} dy={10} data-id="element-6824" data-mp="2cs5mx5fn14yp" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} tickFormatter={value => `$${value.toLocaleString()}`} data-id="element-6825" data-mp="1p2uindz2wsft" />
                  <Tooltip contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }} formatter={(value: number) => [formatSalesMoney(value), salesMetric]} data-id="element-6826" data-mp="1yuh2tztea1up" />
                  <Legend data-id="element-6827" data-mp="11b5tu7lgum3m" />
                  <Line type="monotone" dataKey="value" name={`${salesMetric} sales`} stroke={salesMetric === 'Refunds' ? '#EF4444' : '#0EA5E9'} strokeWidth={2} dot={false} activeDot={{
                r: 6
              }} data-id="element-6828" data-mp="3czu1kthh47lw" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Daily Breakdown Table */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden shadow-sm" data-id="element-6829" data-mp="37ywhucgtf5ia">
            <div className="overflow-x-auto" data-id="element-6830" data-mp="3vp0l2iq3d38r">
              <table className="w-full" data-id="element-6831" data-mp="4ksb79xz01nq">
                <thead className="bg-[#F1F5F9]" data-id="element-6832" data-mp="4ifypcazeji2">
                  <tr data-id="element-6833" data-mp="3mqydtlfrled1">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6834" data-mp="3mpdihtlmlre4">
                      Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6835" data-mp="2bagos6ehye0f">
                      Orders
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6836" data-mp="ltuxq8m9onj5">
                      Gross Sales
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6837" data-mp="gwur9wli9iri">
                      Refunds
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6838" data-mp="81yaoizgh3tm">
                      Net Sales
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-6839" data-mp="3ubr3h59xfo3v">
                  {[...salesRows].reverse().map(row => <tr key={row.date} className="hover:bg-[#F8FAFC] transition-colors" data-id="element-6840" data-mp="3pcnytlxuzjwf">
                      <td className="px-6 py-4 text-sm text-[#0F172A] font-medium" data-id="element-6841" data-mp="3lasvkbwdawg0">
                        {formatSalesDay(row.date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#475569] text-right" data-id="element-6842" data-mp="1jhd3dakmt9ix">
                        {row.orders}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#0F172A] text-right" data-id="element-6843" data-mp="31d5fsd6x2rji">
                        {formatSalesMoney(row.gross)}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#475569] text-right" data-id="element-6844" data-mp="3b6cifqa9vtfw">
                        {formatSalesMoney(row.refunds)}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#0F172A] text-right font-medium" data-id="element-6845" data-mp="2ignjdih4i3gx">
                        {formatSalesMoney(row.net)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div> : activeTab === 'Stock Forecast' ? <div className="space-y-6" data-id="element-6846" data-mp="2wgf19uarg407">
          {/* Time Horizon Selector */}
          <div className="flex justify-start" data-id="element-6847" data-mp="6r3adb5yt80a">
            <div className="bg-[#E2E8F0] p-1 rounded-lg inline-flex" data-id="element-6848" data-mp="6qbdzy4jn68y">
              {timeHorizons.map(horizon => <button key={horizon} onClick={() => setTimeHorizon(horizon)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${timeHorizon === horizon ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#475569]'}`} data-id="element-6849" data-mp="bocvwfq8gziu">
                  {horizon}
                </button>)}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-6850" data-mp="3njagyafy3q6j">
            <StatCard label="Total Units Required" value={stats.totalUnits.toLocaleString()} data-id="element-6851" data-mp="632eqv4qmxlg" />
            <StatCard label="Products Needed" value={stats.productsNeeded} data-id="element-6852" data-mp="15acb76gvt30" />
            <StatCard label="Estimated Revenue" value={`$${stats.estimatedRevenue.toLocaleString()}`} data-id="element-6853" data-mp="f4jq6077ab3t" />
            <StatCard label="Subscriptions Due" value={stats.subscriptionsDue.toLocaleString()} data-id="element-6854" data-mp="b2ygsotvkeyd" />
          </div>

          {/* Actions Bar */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 shadow-sm" data-id="element-6855" data-mp="2gwogx8cyr2cy">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-6856" data-mp="2cvczhq48tnfu">
              <div className="w-full lg:w-80 flex-shrink-0" data-id="element-6857" data-mp="22q4qdskn2kox">
                <SearchBar placeholder="Search products..." value={searchQuery} onChange={setSearchQuery} className="w-full" data-id="element-6858" data-mp="26ryl7k85kuh6" />
              </div>
              <div className="flex items-center gap-3 flex-shrink-0 ml-auto" data-id="element-6859" data-mp="1sziarycx9u68">
                <ExportButton onExport={async type => console.log(`Exporting ${type}`)} data-id="element-6860" data-mp="1xz4u8pjcg6nh" />
                <TableFilter filters={[]} // Placeholder for now
            onFilterChange={() => {}} onFilterRemove={() => {}} onToggleDropdown={() => {}} addedFilterIds={new Set()} onAddFilter={() => {}} renderOnlyButton={true} data-id="element-6861" data-mp="1j2nj9zf2gu1g" />
              </div>
            </div>
          </div>

          {/* Stock Forecast Table */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden shadow-sm" data-id="element-6862" data-mp="1f1tr2tulct2r">
            <div className="overflow-x-auto" data-id="element-6863" data-mp="34grxquv9f4ca">
              <table className="w-full" data-id="element-6864" data-mp="34dvyn37oe9m9">
                <thead className="bg-[#F1F5F9]" data-id="element-6865" data-mp="39f37z6pgvgv1">
                  <tr data-id="element-6866" data-mp="39cqogp428yfv">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6867" data-mp="gtvd2h9l222s">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6868" data-mp="lmerfgg3h18n">
                      SKU
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6869" data-mp="bvad8iy8tkev">
                      Units Required
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6870" data-mp="14i4u75vuq1nl">
                      Current Stock
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6871" data-mp="ukgxwbilkm3b">
                      Stock Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6872" data-mp="3fb91apt5hy8r">
                      Surplus/Shortfall
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-6873" data-mp="32teraw1hdduq">
                  {processedData.map(product => <tr key={product.sku} className="hover:bg-[#F8FAFC] transition-colors" data-id="element-6874" data-mp="3hqfjf0nl3atq">
                      <td className="px-6 py-4" data-id="element-6875" data-mp="3cq9onbm0p87f">
                        <button onClick={() => navigate('/operations/product-replace', {
                    state: {
                      preSelectedProduct: product.sku
                    }
                  })} className="text-sm font-medium text-[#0EA5E9] hover:text-[#0284C7] hover:underline text-left" data-id="element-6876" data-mp="3crkqv6zte7wf">
                          {product.name}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#475569] font-mono" data-id="element-6877" data-mp="2ty8irpatshoi">
                        {product.sku}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#0F172A] font-medium text-right" data-id="element-6878" data-mp="21cfo62zoo9ct">
                        {product.required}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#475569] text-right" data-id="element-6879" data-mp="2qxyt0rzmwu45">
                        {product.stock}
                      </td>
                      <td className="px-6 py-4" data-id="element-6880" data-mp="2zt4wvlfch241">
                        <Badge variant={getStatusBadgeVariant(product.status)} data-id="element-6881" data-mp="34r6o4tmxc7ql">
                          {product.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-6882" data-mp="2830dnq3bml4e">
                        <span className={`text-sm font-medium ${product.delta >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`} data-id="element-6883" data-mp="2c3k7oksll8mb">
                          {product.delta > 0 ? '+' : ''}
                          {product.delta}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div> : activeTab === 'Product Performance' ? <div className="space-y-6" data-id="element-6884" data-mp="18mt5jcv2ns3">
          {/* Controls Row */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-6885" data-mp="1lrgu8so0b27t">
            <div className="bg-[#E2E8F0] p-1 rounded-lg inline-flex" data-id="element-6886" data-mp="1gt5k0gtsupp2">
              {['Last 7 Days', 'Last 30 Days', 'Last 90 Days', 'This Year', 'Custom'].map(range => <button key={range} onClick={() => setDateRange(range)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${dateRange === range ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#475569]'}`} data-id="element-6887" data-mp="1c0mabuy8gepj">
                  {range}
                </button>)}
            </div>

            <div className="relative" data-id="element-6888" data-mp="t3kybq69hec3">
              <select value={salesMode} onChange={e => setSalesMode(e.target.value as SalesMode)} className="appearance-none bg-white border border-[#CBD5E1] rounded-md pl-4 pr-10 py-1.5 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] cursor-pointer" data-id="element-6889" data-mp="t4mf8b9ykv1q">
                <option value="subscription" data-id="element-6890" data-mp="2iju77mkq8pam">Subscription Sales</option>
                <option value="all" data-id="element-6891" data-mp="2qjqfkgr8c1hi">All Sales</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-6892" data-mp="2qhmdm4cw5cjv" />
            </div>
          </div>

          {/* Custom Date Range Picker - only when Custom is selected */}
          {dateRange === 'Custom' && <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 shadow-sm" data-id="element-6893" data-mp="2lj2qi0mk9upx">
              <div className="max-w-sm" data-id="element-6894" data-mp="27rxynkpfq6q5">
                <DateRangePicker label="Custom date range" startDate={customStartDate} endDate={customEndDate} onChange={(start, end) => {
            setCustomStartDate(start);
            setCustomEndDate(end);
          }} data-id="element-6895" data-mp="27qwbihv59ts2" />
              </div>
            </div>}

          {/* Stat Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-6896" data-mp="22u5y62aq5cac">
            {salesMode === 'subscription' ? <>
                <StatCard label="Units Sold" value="847" data-id="element-6897" data-mp="1svgogwszey56" />
                <StatCard label="Revenue" value="$18,420" data-id="element-6898" data-mp="1sux3t8by5vng" />
                <StatCard label="Unique Products" value="24" data-id="element-6899" data-mp="1a6mnja21ik1l" />
                <StatCard label="Active Subscriptions" value="156" data-id="element-6900" data-mp="1a993ny2zy7vf" />
              </> : <>
                <StatCard label="Units Sold" value="2,340" data-id="element-6901" data-mp="15ao480vkjs81" />
                <StatCard label="Revenue" value="$52,600" data-id="element-6902" data-mp="1j8vwvwlko7de" />
                <StatCard label="Unique Products" value="42" data-id="element-6903" data-mp="1e99j9m1u0p42" />
                <StatCard label="Orders" value="389" data-id="element-6904" data-mp="1e7oobvblv45o" />
              </>}
          </div>

          {/* Main Content Area */}
          <div className="flex flex-col lg:flex-row gap-6" data-id="element-6905" data-mp="1o775y541mduk">
            {/* Category Sidebar */}
            <div className="w-full lg:w-64 flex-shrink-0 bg-white rounded-lg border border-[#E2E8F0] shadow-sm overflow-hidden h-fit" data-id="element-6906" data-mp="1o5csfbv3po0i">
              <div className="px-4 py-3 bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-6907" data-mp="1xwqqdf7emfrj">
                <h3 className="text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6908" data-mp="2bwi89r11kgat">
                  Categories
                </h3>
              </div>
              <div className="p-2" data-id="element-6909" data-mp="26vkl1016cw2g">
                {(salesMode === 'subscription' ? subscriptionCategories : allSalesCategories).map(category => <button key={category.name} onClick={() => setSelectedCategory(category.name)} className={`w-full px-4 py-2.5 text-left text-[14px] flex justify-between items-center rounded-md transition-colors ${selectedCategory === category.name ? 'bg-[#E0F2FE] text-[#0EA5E9] font-medium' : 'text-[#475569] hover:bg-[#F8FAFC]'}`} data-id="element-6910" data-mp="18lckw26xjd4x">
                    <span data-id="element-6911" data-mp="1n9z1s8c7gcy9">{category.name}</span>
                    {category.count !== null && <span className={`text-[12px] ${selectedCategory === category.name ? 'text-[#0EA5E9]' : 'text-[#94A3B8]'}`} data-id="element-6912" data-mp="1s9243l0sw52j">
                        {category.count}
                      </span>}
                  </button>)}
              </div>
            </div>

            {/* Right Content */}
            <div className="flex-1 min-w-0" data-id="element-6913" data-mp="1y7b46ogb78e">
              {/* Actions Bar */}
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 shadow-sm mb-6" data-id="element-6914" data-mp="6w8rgjuhssg6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-6915" data-mp="3vi3t4572r3dv">
                  <div className="w-full sm:w-80 flex-shrink-0" data-id="element-6916" data-mp="3qrx6uc01buys">
                    <SearchBar placeholder="Search products..." value={perfSearchQuery} onChange={setPerfSearchQuery} className="w-full" data-id="element-6917" data-mp="3qqc96difbvwp" />
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-auto" data-id="element-6918" data-mp="3guir6elxra7j">
                    <ExportButton onExport={async type => console.log(`Exporting ${type}`)} data-id="element-6919" data-mp="icdfxjmu6omw" />
                    <TableFilter filters={[]} onFilterChange={() => {}} onFilterRemove={() => {}} onToggleDropdown={() => {}} addedFilterIds={new Set()} onAddFilter={() => {}} renderOnlyButton={true} data-id="element-6920" data-mp="dc8zftlxhtwo" />
                  </div>
                </div>
              </div>

              {/* Product Table */}
              <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden shadow-sm" data-id="element-6921" data-mp="2t4eny8cntch2">
                <div className="overflow-x-auto" data-id="element-6922" data-mp="2p1s34reeortd">
                  <table className="w-full" data-id="element-6923" data-mp="2p109hm4dd45q">
                    <thead className="bg-[#F1F5F9]" data-id="element-6924" data-mp="s15qjwm9pjv2">
                      <tr data-id="element-6925" data-mp="s3ryhxd2m1dt">
                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6926" data-mp="x0id0g988ox4">
                          Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6927" data-mp="j3bxcujsv6c4">
                          SKU
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6928" data-mp="1brra1afglcka">
                          Units Sold
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6929" data-mp="11zl6r4jrb8ps">
                          Revenue
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6930" data-mp="1unqve2dxlhuw">
                          Avg Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6931" data-mp="28szk3nwdf1ug">
                          Trend
                        </th>
                        {salesMode === 'subscription' && <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6932" data-mp="2ijcdm5m5y5cq">
                            Active Subs
                          </th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]" data-id="element-6933" data-mp="1ltuxewpk10ws">
                      {perfData.map(product => <tr key={product.sku} className="hover:bg-[#F8FAFC] transition-colors" data-id="element-6934" data-mp="1zt2zbxd3qn4o">
                          <td className="px-6 py-4" data-id="element-6935" data-mp="1uu9ne9dgr46m">
                            <div className="flex items-center gap-3" data-id="element-6936" data-mp="1usoqid3of95l">
                              <div className="w-8 h-8 rounded bg-[#F1F5F9] flex items-center justify-center flex-shrink-0 text-[#94A3B8]" data-id="element-6937" data-mp="3b7q9oftoyrln">
                                <Package className="w-4 h-4" data-id="element-6938" data-mp="3696pokynrpvx" />
                              </div>
                              <span className="text-sm font-medium text-[#0F172A]" data-id="element-6939" data-mp="z60ru1gjy2m5">
                                {product.name}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#475569] font-mono" data-id="element-6940" data-mp="l4y3tj5zyi8l">
                            {product.sku}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#0F172A] text-right" data-id="element-6941" data-mp="1hup98dfttpt6">
                            {salesMode === 'all' ? product.allUnits.toLocaleString() : product.unitsSold.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#0F172A] text-right font-medium" data-id="element-6942" data-mp="143ty0uhuuwmf">
                            $
                            {salesMode === 'all' ? product.allRevenue.toLocaleString() : product.revenue.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#475569] text-right" data-id="element-6943" data-mp="1wqmz5dz5pxwy">
                            ${product.avgPrice.toFixed(2)}
                          </td>
                          <td className="px-6 py-4" data-id="element-6944" data-mp="292z57o7qlm6f">
                            {product.trend !== null ? <div className={`flex items-center gap-1 text-sm font-medium ${product.trend > 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`} data-id="element-6945" data-mp="2e10nw8193jhd">
                                {product.trend > 0 ? <ArrowUp className="w-4 h-4" data-id="element-6946" data-mp="2n3026skvs9gm" /> : <ArrowDown className="w-4 h-4" data-id="element-6947" data-mp="2n1ysosjijv10" />}
                                {Math.abs(product.trend)}%
                              </div> : <div className="flex items-center gap-1 text-sm font-medium text-[#94A3B8]" data-id="element-6948" data-mp="2rz8gy4v620n9">
                                <Minus className="w-4 h-4" data-id="element-6949" data-mp="31regbznqzkch" />
                              </div>}
                          </td>
                          {salesMode === 'subscription' && <td className="px-6 py-4 text-sm text-[#0F172A] text-right" data-id="element-6950" data-mp="35ukkn1rav1gb">
                              {product.activeSubs}
                            </td>}
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div> : activeTab === 'Recurring Revenue' ? <div className="space-y-6" data-id="element-6951" data-mp="1kfwcl4pi6s3p">
          {/* Controls Row */}
          <div className="flex justify-between items-center" data-id="element-6952" data-mp="1fowg20kaqc1q">
            <div className="bg-[#E2E8F0] p-1 rounded-lg inline-flex" data-id="element-6953" data-mp="1fnd0kc6gs510">
              {revenueHorizons.map(horizon => <button key={horizon} onClick={() => setRevenueHorizon(horizon)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${revenueHorizon === horizon ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#475569]'}`} data-id="element-6954" data-mp="1uc97g5wsbc01">
                  {horizon}
                </button>)}
            </div>
            <ExportButton onExport={async type => console.log(`Exporting ${type}`)} data-id="element-6955" data-mp="907na6os1gzq" />
          </div>

          <div className="flex flex-col lg:flex-row gap-6" data-id="element-6956" data-mp="41mnu9hcn1cc">
            {/* Left Column: Stats */}
            <div className="w-full lg:w-64 flex-shrink-0 space-y-4" data-id="element-6957" data-mp="2fozuj3bapt77">
              <StatCard label="Forecast Revenue" value={`$${revenueStats.forecastRevenue.toLocaleString()}`} secondaryValue="renewal income in this period" data-id="element-6958" data-mp="2fqt3ip9dspid" />
              <StatCard label="Renewal Orders" value={revenueStats.renewalOrders.toLocaleString()} secondaryValue="subscription renewals due" data-id="element-6959" data-mp="26vd1vm2dx628" />
              <StatCard label="Avg Renewal Amount" value={`$${revenueStats.avgRenewalAmount.toFixed(2)}`} secondaryValue="average order value" data-id="element-6960" data-mp="2avn9f35ts4j7" />
            </div>

            {/* Right Column: Chart */}
            <div className="flex-1 bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm min-h-[400px]" data-id="element-6961" data-mp="21uyxqf3wmg9h">
              <h3 className="text-[16px] font-semibold text-[#0F172A] mb-6" data-id="element-6962" data-mp="1rz5iup2pqaoj">
                Revenue Forecast
              </h3>
              <div className="h-[320px] w-full" data-id="element-6963" data-mp="3ibbgs6k8umbd">
                <ResponsiveContainer width="100%" height="100%" data-id="element-6964" data-mp="3i9h0x6ntxke5">
                  <LineChart data={chartData} margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-6965" data-mp="3mbvilnfrie3u">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-6966" data-mp="2sn65jl951s3e" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fill: '#64748B',
                  fontSize: 12
                }} dy={10} data-id="element-6967" data-mp="2nntf6qj262w2" />
                    <YAxis yAxisId="left" orientation="left" axisLine={false} tickLine={false} tick={{
                  fill: '#64748B',
                  fontSize: 12
                }} label={{
                  value: 'Orders',
                  angle: -90,
                  position: 'insideLeft',
                  fill: '#64748B',
                  fontSize: 12
                }} data-id="element-6968" data-mp="2enod8vwmvwpa" />
                    <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                  fill: '#64748B',
                  fontSize: 12
                }} tickFormatter={value => `$${value}`} label={{
                  value: 'Revenue',
                  angle: 90,
                  position: 'insideRight',
                  fill: '#64748B',
                  fontSize: 12
                }} data-id="element-6969" data-mp="1uznwsafh8pze" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }} data-id="element-6970" data-mp="fgg7r78fblqw" />
                    <Legend data-id="element-6971" data-mp="3ivlpufc75e3p" />
                    <Line yAxisId="left" type="monotone" dataKey="orders" name="Renewal Orders" stroke="#10B981" strokeWidth={2} dot={false} activeDot={{
                  r: 6
                }} data-id="element-6972" data-mp="3nvzzg1tg4w3u" />
                    <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#F59E0B" strokeWidth={2} dot={false} activeDot={{
                  r: 6
                }} data-id="element-6973" data-mp="1hglu7capkyf" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Product Breakdown Table */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden shadow-sm" data-id="element-6974" data-mp="2x3ix1mvid8rk">
            <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-6975" data-mp="2t3idjntjy7dn">
              <h3 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-6976" data-mp="36u3wfcj0ahbj">
                Product Breakdown
              </h3>
            </div>
            <div className="overflow-x-auto" data-id="element-6977" data-mp="31y5arwp2bdet">
              <table className="w-full" data-id="element-6978" data-mp="2e5pz6yzqrp11">
                <thead className="bg-[#F1F5F9]" data-id="element-6979" data-mp="2e8kgwirdevqr">
                  <tr data-id="element-6980" data-mp="2a5o4xawc5xvs">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6981" data-mp="2a67m2pe5wi8r">
                      Product
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider group cursor-help" data-id="element-6982" data-mp="2j5b7w14prbqy">
                      <div className="flex items-center justify-end gap-1" data-id="element-6983" data-mp="3cloz675bcbn">
                        Subscription Count
                        <div className="relative group" title="Number of active subscriptions that include this product" data-id="element-6984" data-mp="3un3wf3se8xov">
                          <Info className="w-3.5 h-3.5 text-[#94A3B8]" data-id="element-6985" data-mp="1lbtifh6o0dga" />
                        </div>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6986" data-mp="17e4uu1l7qs7j">
                      Forecast Units
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6987" data-mp="20292utr9bhde">
                      Forecast Revenue
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6988" data-mp="1qbxvex8d6q1r">
                      <div className="flex items-center justify-end gap-1" data-id="element-6989" data-mp="1v8odfq3qbvpt">
                        Avg Recurring Line Total
                        <div className="relative group" title="Average revenue generated per subscription per renewal for this product" data-id="element-6990" data-mp="2ixqtitiptgi7">
                          <Info className="w-3.5 h-3.5 text-[#94A3B8]" data-id="element-6991" data-mp="nostvgzswcvx" />
                        </div>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-6992" data-mp="ivhhl0c7z3wm">
                      <div className="flex items-center justify-end gap-1" data-id="element-6993" data-mp="8zehs14ubhcu">
                        Avg Lifetime Value
                        <div className="relative group" title="Average total revenue this product has generated per subscriber across all their orders" data-id="element-6994" data-mp="16nolckk9yd62">
                          <Info className="w-3.5 h-3.5 text-[#94A3B8]" data-id="element-6995" data-mp="11ov90vgq4s7h" />
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-6996" data-mp="sn43bqm6oldm">
                  {revenueTableData.map(product => <tr key={product.name} className="hover:bg-[#F8FAFC] transition-colors" data-id="element-6997" data-mp="3hgj2ui0fy4m2">
                      <td className="px-6 py-4" data-id="element-6998" data-mp="3dc1lsqlgu5ez">
                        <div className="flex items-center gap-3" data-id="element-6999" data-mp="1sw3rlv45bpzu">
                          <div className="w-8 h-8 rounded bg-[#F1F5F9] flex items-center justify-center flex-shrink-0 text-[#94A3B8]" data-id="element-7000" data-mp="1sthbh736w260">
                            <Package className="w-4 h-4" data-id="element-7001" data-mp="21vqgxijlp5cj" />
                          </div>
                          <span className="text-sm font-medium text-[#0F172A]" data-id="element-7002" data-mp="26mp2oxncyvn6">
                            {product.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#475569] text-right font-mono" data-id="element-7003" data-mp="2gkmws1rwfmnp">
                        {product.subscriptionCount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#0F172A] text-right" data-id="element-7004" data-mp="2plkww8z7va10">
                        {product.forecastUnits.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#0F172A] text-right font-medium" data-id="element-7005" data-mp="qb3aav0dqaod">
                        ${product.forecastRevenue.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#475569] text-right" data-id="element-7006" data-mp="16zsa4vc2ftnn">
                        ${product.avgRecurring.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#475569] text-right" data-id="element-7007" data-mp="1gx6pssq60eew">
                        ${product.avgLTV.toFixed(2)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div> : activeTab === 'Retention Health' ? <div className="space-y-6" data-id="element-7008" data-mp="t6swkyvug9iq">
          {/* Retention Health Tab Content */}

          {/* Retention Score Card */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-7009" data-mp="y3ayzs3pbvrw">
            <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-7010" data-mp="12xf7wgwj2dvm">
              {/* Left: Gauge */}
              <div className="flex items-center gap-6" data-id="element-7011" data-mp="5em4wd12cqpn">
                <div className="relative w-32 h-32 flex items-center justify-center" data-id="element-7012" data-mp="aerqbbgqpgza">
                  {/* Background Ring */}
                  <svg className="w-full h-full transform -rotate-90" data-id="element-7013" data-mp="2np9y78xlktqv">
                    <circle cx="64" cy="64" r="56" fill="none" stroke="#F1F5F9" strokeWidth="12" data-id="element-7014" data-mp="2jpj16nhh9hdw" />
                    {/* Foreground Ring (78%) */}
                    <circle cx="64" cy="64" r="56" fill="none" stroke="#0EA5E9" strokeWidth="12" strokeDasharray={`${2 * Math.PI * 56}`} strokeDashoffset={`${2 * Math.PI * 56 * (1 - 0.78)}`} strokeLinecap="round" data-id="element-7015" data-mp="2er7rccr6n6vo" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-7016" data-mp="2008j52u9ekcm">
                    <span className="text-4xl font-bold text-[#0F172A]" data-id="element-7017" data-mp="1vy3ukiio4dwu">
                      78
                    </span>
                  </div>
                </div>
                <div data-id="element-7018" data-mp="1m0pp1dya99jb">
                  <div className="text-sm font-medium text-[#64748B] uppercase tracking-wider mb-1" data-id="element-7019" data-mp="1m18vnyihgbi1">
                    Retention Score
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[#D1FAE5] text-[#065F46] text-xs font-medium rounded-full w-fit" data-id="element-7020" data-mp="1h3pg8va1tsoi">
                    <ArrowUp className="w-3 h-3" data-id="element-7021" data-mp="1cddec2egqdi5" />
                    Improving
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:block w-px h-24 bg-[#E2E8F0]" data-id="element-7022" data-mp="12exnm0jcl696" />

              {/* Right: Sparkline */}
              <div className="flex-1 w-full" data-id="element-7023" data-mp="1q400kv31eaxc">
                <div className="h-20 w-full" data-id="element-7024" data-mp="1q51lrsdrodss">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-7025" data-mp="1v3bk2busfuim">
                    <LineChart data={retentionScoreData} data-id="element-7026" data-mp="1v5pc0bu68qly">
                      <Line type="monotone" dataKey="score" stroke="#0EA5E9" strokeWidth={2} dot={false} data-id="element-7027" data-mp="1hb56mxj0s1mv" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-sm text-[#94A3B8] mt-2" data-id="element-7028" data-mp="292b7xhah85dq">
                  Composite score based on churn rate, member tenure,
                  engagement, and cohort performance.
                </p>
              </div>
            </div>
          </div>

          {/* Factor Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-7029" data-mp="3tm6nl51ujofx">
            {/* Card 1: Churn Rate */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-4 relative overflow-hidden" data-id="element-7030" data-mp="c3lsihjcf130">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#EF4444]" data-id="element-7031" data-mp="76c2p0s1knen" />
              <div className="text-sm font-medium text-[#64748B] mb-1" data-id="element-7032" data-mp="3fmf1si5393rd">
                Churn Rate
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-7033" data-mp="3aodipx7nx4fw">
                <span className="text-2xl font-bold text-[#0F172A]" data-id="element-7034" data-mp="3pk37220bltns">4.2%</span>
                <ArrowDown className="w-4 h-4 text-[#EF4444]" data-id="element-7035" data-mp="3kl020hkcbnft" />
              </div>
              <div className="text-xs text-[#94A3B8]" data-id="element-7036" data-mp="14tkl2mwqxgcf">vs 3.8% last month</div>
            </div>

            {/* Card 2: Avg Tenure */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-4 relative overflow-hidden" data-id="element-7037" data-mp="zvaoc7vdi7op">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#10B981]" data-id="element-7038" data-mp="35k3a109ngylm" />
              <div className="text-sm font-medium text-[#64748B] mb-1" data-id="element-7039" data-mp="3ah35kh8viiyh">
                Avg Tenure
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-7040" data-mp="3eiopgmjtrxi6">
                <span className="text-2xl font-bold text-[#0F172A]" data-id="element-7041" data-mp="3jiaz6m0g2vm8">14.2</span>
                <span className="text-sm text-[#64748B]" data-id="element-7042" data-mp="3jg6rdszfdcgo">months</span>
                <ArrowUp className="w-4 h-4 text-[#10B981]" data-id="element-7043" data-mp="3oh4emjzakwp1" />
              </div>
              <div className="text-xs text-[#94A3B8]" data-id="element-7044" data-mp="3tffrl3kvvnbh">vs 13.1 last month</div>
            </div>

            {/* Card 3: Engagement Rate */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-4 relative overflow-hidden" data-id="element-7045" data-mp="24fe5hn689d5">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#10B981]" data-id="element-7046" data-mp="b2rfoepksrkd" />
              <div className="text-sm font-medium text-[#64748B] mb-1" data-id="element-7047" data-mp="b0n2f65seg7f">
                Engagement Rate
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-7048" data-mp="q5hp5e94t17o">
                <span className="text-2xl font-bold text-[#0F172A]" data-id="element-7049" data-mp="q72fsssbs009">62%</span>
                <ArrowUp className="w-4 h-4 text-[#10B981]" data-id="element-7050" data-mp="l5wl4pf3u2o3" />
              </div>
              <div className="text-xs text-[#94A3B8]" data-id="element-7051" data-mp="g8mq8u8y4ysv">vs 58% last month</div>
            </div>

            {/* Card 4: Cohort Quality */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-4 relative overflow-hidden" data-id="element-7052" data-mp="c7t528mw1y3o">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#94A3B8]" data-id="element-7053" data-mp="18y3dldhplpi8" />
              <div className="text-sm font-medium text-[#64748B] mb-1" data-id="element-7054" data-mp="13xxsyh9ux39n">
                Cohort Quality
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-7055" data-mp="v4u99r8mbu6k">
                <span className="text-2xl font-bold text-[#0F172A]" data-id="element-7056" data-mp="v4cfd19fhs1t">Good</span>
                <Minus className="w-4 h-4 text-[#94A3B8]" data-id="element-7057" data-mp="1rns50wmcqnsu" />
              </div>
              <div className="text-xs text-[#94A3B8]" data-id="element-7058" data-mp="1mvi7oviwqlgj">Stable vs last month</div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-7059" data-mp="3h4dw2uel0ea4">
            {/* Tenure Distribution */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-7060" data-mp="3m271hl82949c">
              <h3 className="text-base font-semibold text-[#0F172A] mb-6" data-id="element-7061" data-mp="37dj9tq1c5eoo">
                Tenure Distribution
              </h3>
              <div className="h-[300px] w-full" data-id="element-7062" data-mp="3casyv4ktbobz">
                <ResponsiveContainer width="100%" height="100%" data-id="element-7063" data-mp="2yccv9mi8ydy2">
                  <BarChart data={tenureDistributionData} margin={{
                top: 20,
                right: 30,
                left: 0,
                bottom: 5
              }} data-id="element-7064" data-mp="33c7kzmqacxhj">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-7065" data-mp="2tebc71ktmeml" />
                    <XAxis dataKey="range" axisLine={false} tickLine={false} tick={{
                  fill: '#64748B',
                  fontSize: 12
                }} dy={10} data-id="element-7066" data-mp="mntyhmzucb0q" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fill: '#64748B',
                  fontSize: 12
                }} data-id="element-7067" data-mp="rku4jxwoxdru" />
                    <Tooltip cursor={{
                  fill: '#F1F5F9'
                }} contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px'
                }} data-id="element-7068" data-mp="276nqm6t0df4b" />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]} data-id="element-7069" data-mp="2h28ukahn0pgp">
                      {tenureDistributionData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.range === '3+y' ? '#0284C7' : '#0EA5E9'} data-id="element-7070" data-mp="2qtvc0okkjkrw" data-mpi={index} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 flex justify-end" data-id="element-7071" data-mp="2zu1qods06qsn">
                <div className="flex items-center gap-2 text-xs text-[#64748B]" data-id="element-7072" data-mp="2zsgu6im4oxs5">
                  <div className="w-3 h-3 bg-[#0284C7] rounded-sm" data-id="element-7073" data-mp="1ag57xhtg8wi9"></div>
                  <span data-id="element-7074" data-mp="15hbrbgiv8pdv">Your most loyal (3+ years)</span>
                </div>
              </div>
            </div>

            {/* Churn Waterfall */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-7075" data-mp="jlim9aoi7g1p">
              <h3 className="text-base font-semibold text-[#0F172A] mb-6" data-id="element-7076" data-mp="okd2q5z00sin">
                Churn Waterfall (Current Month)
              </h3>
              <div className="h-[300px] w-full" data-id="element-7077" data-mp="ycsl34e7jj42">
                <ResponsiveContainer width="100%" height="100%" data-id="element-7078" data-mp="ybhfr05463au">
                  <BarChart data={waterfallChartData} margin={{
                top: 30,
                right: 30,
                left: 0,
                bottom: 5
              }} barCategoryGap="20%" data-id="element-7079" data-mp="pk5fq0w149z">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-7080" data-mp="aln950be1az1" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                  fill: '#64748B',
                  fontSize: 11
                }} dy={10} data-id="element-7081" data-mp="fmsyqp9r4i4c" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fill: '#64748B',
                  fontSize: 12
                }} domain={[328, 372]} ticks={[330, 340, 350, 360, 370]} allowDataOverflow={true} data-id="element-7082" data-mp="1r0eyf4d8m244" />
                    <Tooltip cursor={{
                  fill: '#F1F5F9'
                }} contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px'
                }} formatter={(_value: any, name: string, props: any) => {
                  if (name === 'placeholder') return [null, null];
                  return [props.payload.label, props.payload.name];
                }} data-id="element-7083" data-mp="36l7917omneyc" />
                    <Bar dataKey="placeholder" stackId="a" fill="transparent" isAnimationActive={false} data-id="element-7084" data-mp="2its02w2chze4">
                      {waterfallChartData.map((_entry, index) => <Cell key={`ph-${index}`} fill="transparent" data-id="element-7085" data-mp="2dx1jm7mmp1s6" data-mpi={index} />)}
                    </Bar>
                    <Bar dataKey="value" stackId="a" radius={[4, 4, 0, 0]} isAnimationActive={false} label={{
                  content: (props: any) => {
                    const {
                      x,
                      y,
                      width,
                      index
                    } = props;
                    const item = waterfallChartData[index];
                    if (!item) return null;
                    return <text x={x + width / 2} y={y - 8} textAnchor="middle" fill={item.color === '#EF4444' ? '#EF4444' : item.color === '#10B981' ? '#10B981' : '#0EA5E9'} fontSize={12} fontWeight={600} data-id="element-7087" data-mp="174hh51jflqhc">
                              {item.label}
                            </text>;
                  }
                }} data-id="element-7086" data-mp="h25fpdcyl2qm">
                      {waterfallChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-7088" data-mp="sgwhxipogbep" data-mpi={index} />)}
                    </Bar>
                    <Customized component={(props: any) => {
                  const {
                    formattedGraphicalItems
                  } = props;
                  if (!formattedGraphicalItems || formattedGraphicalItems.length < 2) return null;
                  const valueBarItems = formattedGraphicalItems[1];
                  if (!valueBarItems?.props?.data) return null;
                  const bars = valueBarItems.props.data;
                  const lines: React.ReactElement[] = [];
                  for (let i = 0; i < bars.length - 1; i++) {
                    const currentBar = bars[i];
                    const nextBar = bars[i + 1];
                    if (!currentBar || !nextBar) continue;
                    const item = waterfallChartData[i];
                    const isNegative = item.color === '#EF4444';
                    // For positive/total bars, connector at top (y)
                    // For negative bars, connector at bottom (y + height)
                    let connectorY: number;
                    if (isNegative) {
                      connectorY = currentBar.y + currentBar.height;
                    } else {
                      connectorY = currentBar.y;
                    }
                    const x1 = currentBar.x + currentBar.width;
                    const x2 = nextBar.x;
                    lines.push(<line key={`connector-${i}`} x1={x1} y1={connectorY} x2={x2} y2={connectorY} stroke="#CBD5E1" strokeWidth={1} strokeDasharray="4 3" data-id="element-7090" data-mp="3ip45j6q2u0f1" />);
                  }
                  return <g data-id="element-7091" data-mp="39oxqhgeqcsdr">{lines}</g>;
                }} data-id="element-7089" data-mp="ocoq3cnxafc9" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 text-center" data-id="element-7092" data-mp="34qmjrejqexzr">
                <span className="text-sm font-medium text-[#10B981]" data-id="element-7093" data-mp="2zz4h8m228pco">
                  Net change: +6 members this month
                </span>
              </div>
            </div>
          </div>

          {/* Retention Curve Summary */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-7094" data-mp="2qzhfu09bzjjm">
            <div className="flex items-center justify-between mb-6" data-id="element-7095" data-mp="2lzmo5uhqepxi">
              <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-7096" data-mp="2h0a2ve6idqx1">
                Retention Curve Summary
              </h3>
              <div className="flex items-center gap-4 text-sm" data-id="element-7097" data-mp="wj0vjs9usre4">
                <div className="flex items-center gap-2" data-id="element-7098" data-mp="wklo5ccly09c">
                  <div className="w-3 h-1 bg-[#0EA5E9]" data-id="element-7099" data-mp="11g10fzuyvo3n"></div>
                  <span className="text-[#0F172A]" data-id="element-7100" data-mp="16fvta8yaynrc">Your Club</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-7101" data-mp="1ai0nb8snxmei">
                  <div className="w-3 h-1 border-t border-dashed border-[#94A3B8]" data-id="element-7102" data-mp="1ahh47ora0ryw"></div>
                  <span className="text-[#64748B]" data-id="element-7103" data-mp="dt1dvqgceekq">Industry Average</span>
                </div>
              </div>
            </div>
            <div className="h-[300px] w-full" data-id="element-7104" data-mp="iogn253enmat">
              <ResponsiveContainer width="100%" height="100%" data-id="element-7105" data-mp="mrdaqa7sy8lm">
                <LineChart data={retentionCurveData} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-7106" data-mp="rj4sbv8zd3zm">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-7107" data-mp="3r4jvid2cdf07" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} dy={10} data-id="element-7108" data-mp="3v6ew1fcg7ocx" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} tickFormatter={val => `${val}%`} domain={[0, 100]} data-id="element-7109" data-mp="375kxj9ip7zt2" />
                  <Tooltip contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #E2E8F0',
                borderRadius: '8px'
              }} formatter={(val: number) => [`${val}%`, '']} data-id="element-7110" data-mp="3h1o915zfxaso" />
                  <Line type="monotone" dataKey="club" name="Your Club" stroke="#0EA5E9" strokeWidth={2} dot={{
                r: 4,
                fill: '#0EA5E9',
                strokeWidth: 2,
                stroke: '#fff'
              }} activeDot={{
                r: 6
              }} data-id="element-7111" data-mp="3purn9ghwtrod" />
                  <Line type="monotone" dataKey="industry" name="Industry Average" stroke="#94A3B8" strokeWidth={2} strokeDasharray="5 5" dot={false} activeDot={{
                r: 6
              }} data-id="element-7112" data-mp="2aej0tewm1cy9" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex justify-end" data-id="element-7113" data-mp="pvozq5lgsyij">
              <button onClick={() => navigate('/dashboard/retention')} className="flex items-center gap-1 text-sm font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-7114" data-mp="b7lr0og1f8q1">
                View full cohort analysis <ArrowRight className="w-4 h-4" data-id="element-7115" data-mp="264e1exxfn5k" />
              </button>
            </div>
          </div>

          {/* Insight Card */}
          <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-lg p-4 flex items-start gap-3" data-id="element-7116" data-mp="69snk364cstg">
            <Lightbulb className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-7117" data-mp="3off4cmv56wzg" />
            <p className="text-sm text-[#92400E]" data-id="element-7118" data-mp="3te082w9lrysn">
              Your 6-12 month members are your highest risk cohort this quarter.
              Consider a re-engagement campaign targeting members approaching
              their first anniversary.
            </p>
          </div>
        </div> : activeTab === 'Wine Journey' ? <div className="space-y-6" data-id="element-7119" data-mp="1q5zs6b0sbcwe">
          {/* Wine Journey Tab Content */}

          {/* Metric Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-7120" data-mp="1l7eryblj8t7y">
            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-4" data-id="element-7121" data-mp="1g7acmowd24jb">
              <div className="text-sm font-medium text-[#64748B] mb-1" data-id="element-7122" data-mp="1b7xkppqmu79v">
                Varietal Diversity Score
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-7123" data-mp="28u4qv42l47oc">
                <span className="text-2xl font-bold text-[#0F172A]" data-id="element-7124" data-mp="23vjqn4nc1nzw">
                  6.4 / 10
                </span>
                <ArrowUp className="w-4 h-4 text-[#10B981]" data-id="element-7125" data-mp="1z3s7vgaf4mkb" />
              </div>
              <div className="text-xs text-[#94A3B8]" data-id="element-7126" data-mp="1u6rzuzu0d9qk">vs 5.8 last quarter</div>
            </div>

            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-4" data-id="element-7127" data-mp="2rlnrj494z9n7">
              <div className="text-sm font-medium text-[#64748B] mb-1" data-id="element-7128" data-mp="2mu5rqjiunf3t">
                Discovery Rate
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-7129" data-mp="2v4my1tp5cy1m">
                <span className="text-2xl font-bold text-[#0F172A]" data-id="element-7130" data-mp="2z4n9d5gy8t4g">34%</span>
                <ArrowUp className="w-4 h-4 text-[#10B981]" data-id="element-7131" data-mp="2z6qaolnjy8oh" />
              </div>
              <div className="text-xs text-[#94A3B8]" data-id="element-7132" data-mp="342oudvxxr2ik">vs 28% last quarter</div>
            </div>

            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-4" data-id="element-7133" data-mp="394e9bmoa40bx">
              <div className="text-sm font-medium text-[#64748B] mb-1" data-id="element-7134" data-mp="3e1o47huft475">
                Avg Varietals per Member
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-7135" data-mp="3i1eulkg1xib5">
                <span className="text-2xl font-bold text-[#0F172A]" data-id="element-7136" data-mp="3mv14x5yqta3s">4.2</span>
                <Minus className="w-4 h-4 text-[#94A3B8]" data-id="element-7137" data-mp="3rsj4i5qvecz4" />
              </div>
              <div className="text-xs text-[#94A3B8]" data-id="element-7138" data-mp="nt5slef1zg7">
                Stable vs last quarter
              </div>
            </div>
          </div>

          {/* Product Lifecycle Map - Full Width */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-7139" data-mp="qwt6iz6ooj5t">
            <h3 className="text-base font-semibold text-[#0F172A] mb-6" data-id="element-7140" data-mp="lyhxgqo7qcon">
              Product Lifecycle Map
            </h3>
            <div className="h-[350px] w-full" data-id="element-7141" data-mp="zy94g7gbast5">
              <ResponsiveContainer width="100%" height="100%" data-id="element-7142" data-mp="zvwou0y10uj9">
                <ScatterChart margin={{
              top: 20,
              right: 60,
              bottom: 20,
              left: 0
            }} data-id="element-7143" data-mp="v07p9xm0bdmy">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-7144" data-mp="37qjfbta5y3x" />
                  <XAxis type="number" dataKey="tenure" name="Tenure" unit="m" domain={[0, 24]} tick={{
                fill: '#64748B',
                fontSize: 12
              }} label={{
                value: 'Avg Member Tenure (Months)',
                position: 'bottom',
                offset: 0,
                fill: '#64748B',
                fontSize: 12
              }} data-id="element-7145" data-mp="35xatqz3x3ta" />
                  <YAxis type="number" dataKey="repeatRate" name="Repeat Rate" unit="%" domain={[0, 100]} tick={{
                fill: '#64748B',
                fontSize: 12
              }} label={{
                value: 'Repeat Rate (%)',
                angle: -90,
                position: 'insideLeft',
                fill: '#64748B',
                fontSize: 12
              }} data-id="element-7146" data-mp="3fn2fe6wnysgf" />
                  <ZAxis type="number" dataKey="units" range={[100, 1000]} name="Units" data-id="element-7147" data-mp="1r3et4bzgndbi" />
                  <Tooltip cursor={{
                strokeDasharray: '3 3'
              }} content={({
                active,
                payload
              }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return <div className="bg-white border border-[#E2E8F0] rounded-lg p-3 shadow-lg" data-id="element-7149" data-mp="29ufq4uh0sysf">
                            <p className="font-medium text-[#0F172A] mb-1" data-id="element-7150" data-mp="tgfs5p1qjj7t">
                              {data.name}
                            </p>
                            <p className="text-xs text-[#64748B]" data-id="element-7151" data-mp="18ppsor49sl23">
                              Tenure: {data.tenure} months
                            </p>
                            <p className="text-xs text-[#64748B]" data-id="element-7152" data-mp="1dn83yo5rfcd9">
                              Repeat Rate: {data.repeatRate}%
                            </p>
                            <p className="text-xs text-[#64748B]" data-id="element-7153" data-mp="13q3fuisct6tt">
                              Units: {data.units}
                            </p>
                          </div>;
                }
                return null;
              }} data-id="element-7148" data-mp="2evv9mi3jux8p" />
                  <ReferenceArea x1={0} x2={6} y1={0} y2={40} fill="#E0F2FE" fillOpacity={0.3} stroke="none" label={{
                value: 'Gateway Wines',
                position: 'insideBottomLeft',
                fill: '#0369A1',
                fontSize: 12
              }} data-id="element-7154" data-mp="usjb2zva8seg" />
                  <ReferenceArea x1={12} x2={24} y1={60} y2={100} fill="#DCFCE7" fillOpacity={0.3} stroke="none" label={{
                value: 'Loyalty Wines',
                position: 'insideTopRight',
                fill: '#15803D',
                fontSize: 12
              }} data-id="element-7155" data-mp="71dugihipw33" />
                  <Scatter name="Products" data={productLifecycleData} data-id="element-7156" data-mp="3jlro3dmnzz9k">
                    {productLifecycleData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-7157" data-mp="1g6ek8zvu2q5" data-mpi={index} />)}
                  </Scatter>
                  <Customized component={(props: any) => {
                const {
                  xAxisMap,
                  yAxisMap
                } = props;
                if (!xAxisMap || !yAxisMap) return null;
                const xAxis = Object.values(xAxisMap)[0] as any;
                const yAxis = Object.values(yAxisMap)[0] as any;
                if (!xAxis?.scale || !yAxis?.scale) return null;
                const labelOffsets: Record<string, {
                  dx: number;
                  dy: number;
                }> = {
                  '2024 Rosé': {
                    dx: 12,
                    dy: 4
                  },
                  '2024 Sauv Blanc': {
                    dx: 14,
                    dy: 4
                  },
                  '2023 Shiraz': {
                    dx: 16,
                    dy: -6
                  },
                  '2023 Reserve Cab': {
                    dx: 14,
                    dy: 4
                  },
                  '2024 Pinot Noir': {
                    dx: 14,
                    dy: -8
                  },
                  '2023 Sparkling': {
                    dx: 12,
                    dy: -8
                  }
                };
                return <g data-id="element-7159" data-mp="ff5r32k8ul4d">
                          {productLifecycleData.map((item, i) => {
                    const cx = xAxis.scale(item.tenure);
                    const cy = yAxis.scale(item.repeatRate);
                    const offset = labelOffsets[item.name] || {
                      dx: 12,
                      dy: 4
                    };
                    return <text key={`label-${i}`} x={cx + offset.dx} y={cy + offset.dy} fill="#94A3B8" fontSize={10} textAnchor="start" dominantBaseline="middle" data-id="element-7160" data-mp="2laq9str4sojv" data-mpi={i}>
                                {item.name}
                              </text>;
                  })}
                        </g>;
              }} data-id="element-7158" data-mp="3thcnr549gng5" />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Swap & Drop Patterns - Full Width */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-7161" data-mp="2wmxhvfoa87ae">
            <div className="flex items-center justify-between mb-6" data-id="element-7162" data-mp="2ronptdxutmt0">
              <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-7163" data-mp="2nnalj8aetro7">
                Swap & Drop Patterns
              </h3>
              <div className="flex items-center gap-4 text-xs" data-id="element-7164" data-mp="2ip0kih207wuo">
                <div className="flex items-center gap-1.5" data-id="element-7165" data-mp="1c85drpvf92xq">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#0EA5E9]" data-id="element-7166" data-mp="1c9q9hith2rx6" />
                  <span className="text-[#64748B]" data-id="element-7167" data-mp="178soyzkzplsi">Swapped In</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-7168" data-mp="2v58in5eoxu47">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#F87171]" data-id="element-7169" data-mp="2v3xbcvm1e48c" />
                  <span className="text-[#64748B]" data-id="element-7170" data-mp="2zy1ipfz7se9y">Dropped</span>
                </div>
              </div>
            </div>
            <div className="h-[280px] w-full" data-id="element-7171" data-mp="2m38wxzsqe1ux">
              <ResponsiveContainer width="100%" height="100%" data-id="element-7172" data-mp="2q5vqq68ypwur">
                <BarChart layout="vertical" data={swapDropData} margin={{
              top: 5,
              right: 30,
              left: 40,
              bottom: 5
            }} data-id="element-7173" data-mp="3dw88s9xx99z2">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" data-id="element-7174" data-mp="3isypmzhjw9lj" />
                  <XAxis type="number" hide data-id="element-7175" data-mp="34xnvxpf1luir" />
                  <YAxis type="category" dataKey="name" tick={{
                fill: '#64748B',
                fontSize: 12
              }} width={120} axisLine={false} tickLine={false} data-id="element-7176" data-mp="39tv65ueh1s6i" />
                  <Tooltip cursor={{
                fill: '#F1F5F9'
              }} contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #E2E8F0',
                borderRadius: '8px'
              }} data-id="element-7177" data-mp="1yg0tlnev52vj" />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={24} data-id="element-7178" data-mp="1lwc9zbhdgcsq">
                    {swapDropData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.type === 'in' ? '#0EA5E9' : '#F87171'} data-id="element-7179" data-mp="1zukatuhj3y96" data-mpi={index} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Varietal Diversity Over Time */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-7180" data-mp="129xtdp57mmkn">
            <h3 className="text-base font-semibold text-[#0F172A] mb-6" data-id="element-7181" data-mp="1gy28yk8yuhz7">
              Varietal Diversity Over Time
            </h3>
            <div className="h-[300px] w-full" data-id="element-7182" data-mp="1c5razmxsd69a">
              <ResponsiveContainer width="100%" height="100%" data-id="element-7183" data-mp="ogybjghygg9x">
                <AreaChart data={varietalDiversityData} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0
            }} stackOffset="expand" data-id="element-7184" data-mp="ofesji4wzr4g">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-7185" data-mp="2jijw2mwb8a0r" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} dy={10} data-id="element-7186" data-mp="2ohwlnfekfv71" />
                  <YAxis tickFormatter={val => `${(val * 100).toFixed(0)}%`} axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} data-id="element-7187" data-mp="2xaretaqre0gg" />
                  <Tooltip contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #E2E8F0',
                borderRadius: '8px'
              }} formatter={(value: number, name: string) => {
                const nameMap: Record<string, string> = {
                  shiraz: 'Shiraz',
                  cabernet: 'Cabernet',
                  pinotNoir: 'Pinot Noir',
                  chardonnay: 'Chardonnay',
                  sauvBlanc: 'Sauvignon Blanc',
                  rose: 'Rosé',
                  sparkling: 'Sparkling',
                  other: 'Other'
                };
                return [`${value}%`, nameMap[name] || name];
              }} data-id="element-7188" data-mp="25loajuyvgq3t" />
                  <Area type="monotone" dataKey="shiraz" stackId="1" stroke="#7F1D1D" fill="#991B1B" name="shiraz" data-id="element-7189" data-mp="3qdj18o3ut7zl" />
                  <Area type="monotone" dataKey="cabernet" stackId="1" stroke="#991B1B" fill="#B91C1C" name="cabernet" data-id="element-7190" data-mp="3md07upmbbtva" />
                  <Area type="monotone" dataKey="pinotNoir" stackId="1" stroke="#B91C1C" fill="#DC2626" name="pinotNoir" data-id="element-7191" data-mp="3hbt18tvwp0q7" />
                  <Area type="monotone" dataKey="chardonnay" stackId="1" stroke="#D97706" fill="#F59E0B" name="chardonnay" data-id="element-7192" data-mp="37fq27wwcpi7h" />
                  <Area type="monotone" dataKey="sauvBlanc" stackId="1" stroke="#059669" fill="#10B981" name="sauvBlanc" data-id="element-7193" data-mp="33ffsqa9co9nv" />
                  <Area type="monotone" dataKey="rose" stackId="1" stroke="#DB2777" fill="#EC4899" name="rose" data-id="element-7194" data-mp="15o36ji1ikz4o" />
                  <Area type="monotone" dataKey="sparkling" stackId="1" stroke="#0284C7" fill="#38BDF8" name="sparkling" data-id="element-7195" data-mp="11kx0aaeej7y7" />
                  <Area type="monotone" dataKey="other" stackId="1" stroke="#475569" fill="#94A3B8" name="other" data-id="element-7196" data-mp="1oxczjodzk2vs" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4" data-id="element-7197" data-mp="1g4jaiz8qlp4q">
              {[{
            label: 'Shiraz',
            color: '#991B1B'
          }, {
            label: 'Cabernet',
            color: '#B91C1C'
          }, {
            label: 'Pinot Noir',
            color: '#DC2626'
          }, {
            label: 'Chardonnay',
            color: '#F59E0B'
          }, {
            label: 'Sauvignon Blanc',
            color: '#10B981'
          }, {
            label: 'Rosé',
            color: '#EC4899'
          }, {
            label: 'Sparkling',
            color: '#38BDF8'
          }, {
            label: 'Other',
            color: '#94A3B8'
          }].map(item => <div key={item.label} className="flex items-center gap-1.5" data-id="element-7198" data-mp="31nmye4fc2ymc">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
              backgroundColor: item.color
            }} data-id="element-7199" data-mp="2wp1yy77woiyy" />
                  <span className="text-xs text-[#64748B]" data-id="element-7200" data-mp="3ahi0t4p2maya">{item.label}</span>
                </div>)}
            </div>
            <p className="text-sm text-[#94A3B8] mt-3" data-id="element-7201" data-mp="35o6wpbbn88a2">
              A broadening mix indicates healthy exploration. A narrowing mix
              may signal member fatigue.
            </p>
          </div>

          {/* Insight Card */}
          <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-lg p-4 flex items-start gap-3" data-id="element-7202" data-mp="3tfc7hc2q8adi">
            <Lightbulb className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-7203" data-mp="3pe0gb247fh37" />
            <p className="text-sm text-[#92400E]" data-id="element-7204" data-mp="3pbwfit1lqy75">
              Your 2024 Rosé has a high gateway rate but very low repeat
              purchases. Consider featuring it in new member welcome boxes while
              offering Pinot Noir as the step-up recommendation.
            </p>
          </div>
        </div> : <div className="space-y-6" data-id="element-7205" data-mp="yytt0lvmgtfp">
          {/* Cancellation Timing Tab Content */}

          {/* Secondary summary figures */}
          <div className="flex flex-wrap gap-3" data-id="element-7206" data-mp="u7bppr64mgrk">
            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm px-4 py-3 min-w-[220px]" data-id="element-7207" data-mp="u60lxrcol90g">
              <div className="text-xs font-medium text-[#64748B]" data-id="element-7208" data-mp="p765gw26rwji">
                Voluntary cancellations
              </div>
              <div className="text-lg font-semibold text-[#0F172A] mt-0.5" data-id="element-7209" data-mp="g7sx66pste0d">
                47
              </div>
            </div>
            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm px-4 py-3 min-w-[220px]" data-id="element-7210" data-mp="ba0tkffsk3gc">
              <div className="text-xs font-medium text-[#64748B]" data-id="element-7211" data-mp="6bpjq4phxsy4">
                Excluded — payment failures
              </div>
              <div className="text-lg font-semibold text-[#0F172A] mt-0.5" data-id="element-7212" data-mp="25kw9y3x7wmto">
                12
              </div>
            </div>
          </div>

          {/* Cancellations by billing cycle */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-7213" data-mp="293dah50rkimz">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6" data-id="element-7214" data-mp="2d4orwn9hhxk3">
              <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-7215" data-mp="2hy9uksm0vfpm">
                Cancellations by billing cycle
              </h3>
              <div className="inline-flex rounded-lg border border-[#E2E8F0] bg-white p-0.5" role="group" aria-label="Measure cancellations by" data-id="element-7216" data-mp="1q6bwiup8f86w">
                {(['Cycles', 'Months'] as const).map(option => <button key={option} type="button" onClick={() => setCancellationBasis(option)} aria-pressed={cancellationBasis === option} className={`px-3 h-8 rounded-md text-[13px] font-medium transition-colors ${cancellationBasis === option ? 'bg-[#F0F9FF] text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-7217" data-mp="1z707fgjbwsfk">
                    {option}
                  </button>)}
              </div>
            </div>
            <div className="h-[360px] w-full" data-id="element-7218" data-mp="2pwuszxbrv7qm">
              <ResponsiveContainer width="100%" height="100%" data-id="element-7219" data-mp="2pvjmvquutnwc">
                <ComposedChart data={cancellationBasis === 'Cycles' ? cancellationsByCycle : cancellationsByMonth} margin={{
              top: 20,
              right: 20,
              left: 0,
              bottom: 20
            }} data-id="element-7220" data-mp="2kyt8d7yp70d1">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-7221" data-mp="2uwz7xix17ueq" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} dy={10} label={{
                value: cancellationBasis === 'Cycles' ? 'Completed billing cycles' : 'Months since first order',
                position: 'insideBottom',
                offset: -12,
                fill: '#64748B',
                fontSize: 12
              }} data-id="element-7222" data-mp="2774vh2gmk7v0" />
                  <YAxis yAxisId="left" allowDecimals={false} axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} label={{
                value: 'Cancellations',
                angle: -90,
                position: 'insideLeft',
                fill: '#64748B',
                fontSize: 12
              }} data-id="element-7223" data-mp="1jh2af6b4o25w" />
                  <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} label={{
                value: 'Avg. reminders',
                angle: 90,
                position: 'insideRight',
                fill: '#64748B',
                fontSize: 12
              }} data-id="element-7224" data-mp="3l4dge6b92pw9" />
                  <Tooltip cursor={{
                fill: '#F1F5F9'
              }} contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #E2E8F0',
                borderRadius: '8px'
              }} data-id="element-7225" data-mp="8ojn0b9gxba9" />
                  <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{
                paddingBottom: 12,
                fontSize: 12,
                color: '#64748B'
              }} data-id="element-7226" data-mp="2xgdtjhh27jmy" />
                  <Bar yAxisId="left" dataKey="count" name="Cancellations" fill="#0EA5E9" radius={[4, 4, 0, 0]} data-id="element-7227" data-mp="38wju9in7tttg" />
                  <Line yAxisId="right" type="monotone" dataKey="remindersSent" name="Avg. reminders sent" stroke="#F59E0B" strokeWidth={2} dot={{
                r: 3
              }} data-id="element-7228" data-mp="33z0egeavd8ze" />
                  <Line yAxisId="right" type="monotone" dataKey="remindersOpened" name="Avg. reminders opened" stroke="#10B981" strokeWidth={2} dot={{
                r: 3
              }} data-id="element-7229" data-mp="hi7e92x8601" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Supporting figures */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-7230" data-mp="3im3dipeubpou">
            {cancellationTimingSummary.map(item => <div key={item.label} className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-4" data-id="element-7231" data-mp="2baurrm0999wx">
                <div className="text-sm font-medium text-[#64748B] mb-1" data-id="element-7232" data-mp="276eolsi10gle">
                  {item.label}
                </div>
                <div className="text-2xl font-bold text-[#0F172A]" data-id="element-7233" data-mp="qr8up0rlovqe">
                  {item.value}
                </div>
              </div>)}
          </div>
        </div>}
    </div>;
}